import { resolveLicensingApiBases } from '@infra/licence/activationConstants';
import { SecureLicenceStore } from '@infra/licence/SecureLicenceStore';
import { resilientFetch } from '@infra/network/resilientFetch';

export interface ModuleOffer {
  feature_code: string;
  label: string;
  description: string | null;
  amount_xaf: number | null;
  purchasable: boolean;
  included: boolean;
}

export interface NexxusInvoice {
  id: string;
  invoiceNumber: string;
  currency: string;
  totalXaf: number;
  customerSnapshot: Record<string, unknown>;
  items: Array<{ feature_code: string; label: string; amount_xaf: number }>;
  issuedAt: string;
}

interface BillingErrorBody {
  error?: string;
  code?: string;
}

export class NexxusBillingClient {
  private resolvedBase: string | null = null;

  async catalog(): Promise<{ currency: 'XAF'; items: ModuleOffer[] }> {
    return this.request('/v1/billing/modules/catalog', {});
  }

  async createOrder(
    featureCodes: string[],
  ): Promise<{ id: string; totalXaf: number; currency: string }> {
    return this.request('/v1/billing/module-orders', { feature_codes: featureCodes });
  }

  async listInvoices(): Promise<NexxusInvoice[]> {
    const response = await this.request<{ items: NexxusInvoice[] }>('/v1/billing/invoices', {});
    return response.items;
  }

  private async request<T>(path: string, body: Record<string, unknown>): Promise<T> {
    const certificate = await SecureLicenceStore.getActivationCertificate();
    const fingerprint = await SecureLicenceStore.getDeviceFingerprint();
    if (!certificate || !fingerprint) throw new Error('Licence locale indisponible');

    const payload = { certificate, device_fingerprint: fingerprint, ...body };
    const bases = this.resolvedBase ? [this.resolvedBase] : resolveLicensingApiBases();
    let lastError: Error | null = null;

    for (const [index, base] of bases.entries()) {
      try {
        const result = await this.postJson(`${base}${path}`, payload);
        if (result.ok && result.data) {
          this.resolvedBase = base;
          return result.data as T;
        }
        const errorBody = result.data as BillingErrorBody | null;
        const error = new Error(
          (errorBody && errorBody.error) ||
            `Service de facturation indisponible (${result.status})`,
        );
        const canRetry =
          index < bases.length - 1 && [401, 403, 404, 502, 503].includes(result.status);
        if (!canRetry) throw error;
        lastError = error;
      } catch (error) {
        if (error instanceof Error && index < bases.length - 1 && isNetworkOrRetryable(error)) {
          lastError = error;
          continue;
        }
        throw error;
      }
    }

    throw lastError ?? new Error('Service de facturation indisponible');
  }

  /**
   * Préfère l’IPC Electron (processus principal) pour contourner les blocages
   * fetch/CORS du renderer — même chemin que l’activation.
   */
  private async postJson(
    url: string,
    body: Record<string, unknown>,
  ): Promise<{ ok: boolean; status: number; data: unknown }> {
    if (typeof window !== 'undefined' && window.electron?.activateRemote) {
      try {
        return await window.electron.activateRemote(url, body);
      } catch {
        /* IPC indisponible → repli fetch */
      }
    }

    const response = await resilientFetch(
      url,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      },
      { timeoutMs: 15_000, retries: 1 },
    );
    const data = (await response.json().catch(() => null)) as unknown;
    return { ok: response.ok, status: response.status, data };
  }
}

function isNetworkOrRetryable(error: Error): boolean {
  return (
    error.name === 'TimeoutError' ||
    error.name === 'AbortError' ||
    error.message.includes('fetch') ||
    error.message.includes('Failed') ||
    error.message.includes('network')
  );
}
